import { Dispatch } from '@reduxjs/toolkit';
import slice, { IngestState } from './ingestSlice';
import AlertActions from '../alerts/actions';
import IngestGroup from '../../common/domain/IngestGroup';
import { IngestFileType } from '../../common/domain/IngestFileType';
import config from '../../config';
import { getActiveFileTypes } from './fileTypeHelpers';
import { isCollectionNullOrEmpty } from '../../common/utils';
import { AlertType } from '../../common/domain/ApplicationAlert';
import { getActiveFileFormats } from './fileFormatsHelper';
import { translateToIndexMapping } from './mappingHelpers';
import {
  getUploadUrl,
  pollUploadValidation,
  uploadFile,
} from '../../api/ingest';
import fepLinearSchema from '../../pages/IngestPage/schema/FepLinear';
import { getUserInfo } from '../../common/authentication';

const { actions } = slice;

export default class IngestActions {
  constructor(private dispatch: Dispatch) {}

  loadGroups() {
    this.dispatch(actions.loadGroups({ loading: true }));
    getUserInfo().then((userInfo: any) => {
      const groups = userInfo.groups.map((g) => ({ id: g, name: g }));
      this.dispatch(
        actions.loadGroups({
          loading: false,
          items: groups,
        }),
      );
    });
  }

  selectGroup(group: IngestGroup) {
    this.dispatch(
      actions.selectGroup({
        loading: false,
        selection: group,
      }),
    );
  }

  loadFileTypes() {
    this.dispatch(actions.loadFileTypes({ loading: true }));

    const activeFileTypes = getActiveFileTypes(config);

    this.dispatch(
      actions.loadFileTypes({
        loading: false,
        items: activeFileTypes,
      }),
    );

    if (isCollectionNullOrEmpty(activeFileTypes)) {
      new AlertActions(this.dispatch).add({
        header: 'No active instruments',
        body:
          'None of the instrument related feature flags have been marked as active.',
        type: AlertType.INFO,
      });
    }
  }

  selectFileType(fileType: IngestFileType) {
    this.dispatch(
      actions.selectFileType({
        loading: false,
        selection: fileType,
      }),
    );
  }

  loadFileFormats() {
    this.dispatch(
      actions.loadFileFormats({
        loading: true,
      }),
    );

    this.dispatch(
      actions.loadFileFormats({
        loading: false,
        items: getActiveFileFormats(config),
      }),
    );
  }

  selectUploadFile(file: File | null | undefined) {
    if (file == null) return;
    this.dispatch(
      actions.selectUploadFile({ selection: file, loading: false }),
    );
  }

  upload(state: IngestState) {
    const alertActions = new AlertActions(this.dispatch);
    let errors = 0;

    if (state.fileToUpload == null) {
      alertActions.add({
        header: 'Selection Required',
        body: 'Please select a file.',
        type: AlertType.INFO,
      });
      errors++;
    } else {
      if (!/^.+\.csv$/gi.test(state.fileToUpload.name)) {
        alertActions.add({
          header: 'Invalid Selection',
          body: 'Invalid file type, please use .csv files only.',
          type: AlertType.ERROR,
        });
        errors++;
      }
    }

    if (state.groupSelector.selectedItem == null) {
      alertActions.add({
        header: 'Selection Required',
        body: 'Please select an upload group.',
        type: AlertType.INFO,
      });
      errors++;
    }

    if (state.fileTypeSelector.selectedItem == null) {
      alertActions.add({
        header: 'Selection Required',
        body: 'Please select a file type.',
        type: AlertType.INFO,
      });
      errors++;
    }

    if (errors > 0) return;
    this.dispatch(
      actions.updateProgress({
        message: 'Getting upload parameters...',
        type: '',
        value: 50,
      }),
    );

    if (
      state.fileToUpload != null &&
      state.groupSelector.selectedItem != null &&
      state.fileTypeSelector.selectedItem != null
    )
      getUploadUrl({
        fileName: state.fileToUpload?.name,
        groupId: state.groupSelector.selectedItem?.item.id,
        uploadType: state.fileTypeSelector.selectedItem.item,
      })
        .then((result) => {
          this.dispatch(
            actions.updateProgress({
              message: 'Received upload parameters...',
              type: '',
              value: 100,
            }),
          );
          return uploadFile(
            result.url,
            state.fileToUpload as File,
            (loaded, total) => {
              const progress = Math.floor(100 * (loaded / total));
              this.dispatch(
                actions.updateProgress({
                  message: `Uploading ${progress}%`,
                  type: '',
                  value: progress,
                }),
              );
            },
          ).then(() => {
            this.dispatch(
              actions.updateProgress({
                message: 'Checking for previous uploads...',
                type: '',
                value: 100,
              }),
            );
            const m = pollUploadValidation(
              result.ingestId,
              state.groupSelector.selectedItem?.id as string,
              (x) => x.validatedAt > 0,
              (x) => x.errors?.length > 0,
            );
            return m;
          });
        })
        .then((response) => {
          const fileType = state.fileTypeSelector.selectedItem
            ?.item as IngestFileType;

          this.dispatch(
            actions.initializeMapping({
              isCancellable: true,
              cancelling: false,
              group: state.groupSelector.selectedItem?.item as IngestGroup,
              cols: response.previewRows[0].r.length,
              rows: response.rowCount,
              fileName: response.fileName,
              type: fileType,
              hasHeader: true,
              previewRows: response.previewRows.map((r) => r.r),
              mapping: translateToIndexMapping(response.mappedColumns),
              schema: fepLinearSchema,
              ingestId: response.ingestId,
            }),
          );
        })
        .catch((error) => {
          this.dispatch(actions.resetUpload());
          if (error.errors != null && error.errors.length > 0) {
            error.errors.forEach((r) => {
              alertActions.add({
                header: 'Error',
                body: r.message || r.toString(),
                type: AlertType.ERROR,
              });
            });
          } else {
            alertActions.add({
              header: 'Error',
              body: error.toString(),
              type: AlertType.ERROR,
            });
          }
        });
  }
}
